import { type KeyAction, onBindingsChange, shortcutTitle } from '../settings/keybindings.js';
import { uiText } from './text.js';

/**
 * Where a HUD button sits: the bottom row holds what is used while playing
 * (inventory, map), the top row what leads out of the game (account, home).
 */
export type HudCorner = 'bottom' | 'top';

/**
 * The row of HUD buttons in a corner. One container per corner so buttons
 * added by different panels sit side by side instead of overlapping.
 */
export function hudBar(corner: HudCorner = 'bottom'): HTMLElement {
  const existing = document.querySelector<HTMLElement>(`.hud--${corner}`);
  if (existing !== null) return existing;
  const bar = document.createElement('div');
  bar.className = `hud hud--${corner}`;
  document.body.append(bar);
  return bar;
}

/**
 * A square button showing one of the generated icons
 * (`data/assets/src/ui/icon-<name>.png`) in a slot frame. The label is for
 * the tooltip and for screen readers: the icon is the whole button.
 */
export function iconButton(icon: string, label: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = 'hud-button hud-button--icon';
  button.type = 'button';
  button.title = uiText(label);
  button.setAttribute('aria-label', uiText(label));
  const image = document.createElement('span');
  image.className = 'hud-button__icon';
  image.style.backgroundImage = `url('/assets/ui/icon-${icon}.png')`;
  button.append(image);
  return button;
}

/**
 * The close button of every panel: the generated cross (`icon-close.png`),
 * pinned to the panel's top-right corner by `.panel-close`. Every window
 * closes the same way, so a new one takes this rather than a « Fermer » link.
 * The panel must be positioned (fixed, absolute or relative).
 */
export function closeButton(label: string, onClose: () => void): HTMLButtonElement {
  const button = iconButton('close', label);
  button.classList.add('panel-close');
  button.addEventListener('click', onClose);
  return button;
}

/** An icon button added to the HUD row of a corner. */
export function hudIconButton(
  icon: string,
  label: string,
  corner: HudCorner = 'bottom',
): HTMLButtonElement {
  const button = iconButton(icon, label);
  hudBar(corner).append(button);
  return button;
}

/**
 * A HUD button that opens a window with a rebindable key: its tooltip names
 * the key (« Inventaire (I) ») and follows it when the player rebinds it in
 * the settings. The listener lets go once the button has left the page.
 */
export function hudShortcutButton(
  icon: string,
  label: string,
  action: KeyAction,
  corner: HudCorner = 'bottom',
): HTMLButtonElement {
  const button = hudIconButton(icon, shortcutTitle(label, action), corner);
  button.dataset.action = action;
  const unsubscribe = onBindingsChange(() => {
    if (!button.isConnected) {
      unsubscribe();
      return;
    }
    const title = uiText(shortcutTitle(label, action));
    button.title = title;
    button.setAttribute('aria-label', title);
  });
  return button;
}
